const CACHE_PREFIX = 'tr_en_v2:'; // bump when cache semantics change

// In-memory cache to avoid repeated work during a session.
const mem = new Map();

const normalizeText = (text) => {
  const src = typeof text === 'string' ? text : '';
  // Normalize for stable caching/hashing across platforms.
  return src.normalize('NFC').trim();
};

const decodeHtmlEntities = (text) => {
  // Treat as plain text; decode a conservative subset + numeric entities.
  const s = typeof text === 'string' ? text : '';
  if (!s.includes('&')) return s;

  const named = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    '#39': "'",
  };

  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (m, ent) => {
    if (named[ent]) return named[ent];
    if (ent.startsWith('#x')) {
      const cp = parseInt(ent.slice(2), 16);
      return Number.isFinite(cp) ? String.fromCodePoint(cp) : m;
    }
    if (ent.startsWith('#')) {
      const cp = parseInt(ent.slice(1), 10);
      return Number.isFinite(cp) ? String.fromCodePoint(cp) : m;
    }
    return m;
  });
};

const readCache = (text) => {
  const key = normalizeText(text);
  if (!key) return null;
  if (mem.has(key)) return mem.get(key);
  try {
    const v = sessionStorage.getItem(CACHE_PREFIX + key);
    if (typeof v === 'string' && v.length > 0) {
      mem.set(key, v);
      return v;
    }
  } catch {
    // ignore
  }
  return null;
};

const writeCache = (text, translated) => {
  const key = normalizeText(text);
  if (!key) return;
  const val = typeof translated === 'string' ? translated : key;
  mem.set(key, val);
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, val);
  } catch {
    // ignore (quota, etc.)
  }
};

const requestViaCloudFunction = async (text) => {
  const res = await fetch('/api/translate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(`translate failed: ${res.status}`);

  const payload = await res.json();
  const out = payload && typeof payload.translatedText === 'string' ? payload.translatedText : '';
  return decodeHtmlEntities(out);
};

export const translateToEnglish = async (text) => {
  const src = normalizeText(text);
  if (!src) return '';

  const cached = readCache(src);
  if (cached) return cached;

  // Prefer our same-origin Cloud Function proxy to guarantee UTF-8 JSON and avoid CSP/CORS issues.
  let translated;
  try {
    translated = await requestViaCloudFunction(src);
  } catch {
    // If the function isn't deployed or is failing, degrade gracefully to the original string.
    return src;
  }

  const out = normalizeText(translated) || src;
  writeCache(src, out);
  return out;
};

export const translateToEnglishCachedSync = (text) => {
  const src = normalizeText(text);
  if (!src) return '';
  return readCache(src);
};
